import { FieldNames } from '~/src/dash/components/list-table';
import { IStore } from '~/src/dash/reducers/store';
import { IListItem } from '~/src/dash/types/menuList';
import {
  alignRight,
  backgroundColor,
  container,
  drawerWidth,
  primaryColor,
  whiteColor,
} from '~/src/dash/utils/material-dashboard-react';

export interface IMeasurementFilter {
  menuTitle: string;
  list: Record<string, IListItem>;
}

export interface IMeasurementDynamicFilter {
  menuTitle: string;
  graph: string;
  showID?: boolean;
  idKey: string;
}

export interface IMeasurementChart {
  title: string;
  graph: string;
}

export interface IMeasurementTableGroupBy {
  label: string;
  // as it appears in the api response
  dataKey: string;
  // as is needed in the request body
  paramKey: string;
  // the direct child paramKey if it is a parent node
  childKey?: string;
  // key with ID at end in pascal case for filtering purposes
  idKey: string;
  // the selected hierarchy tree
  tree: string;
  // parent level hierarchy. 0 is the orginating parent
  nodeLevel: number;
  // sets group by as default
  isDefault?: boolean;
}

export interface IMeasurementTableColumn {
  label: string;
  // as it appears in the api response
  dataKey: FieldNames;
  // set type for listTable
  renderType: 'normal' | 'bignumber' | 'percent';
}

export interface IMeasurementTable {
  graph: string;
  groupBys: IMeasurementTableGroupBy[];
  columns: IMeasurementTableColumn[];
}
interface IMeasurementConfig {
  defaultProps: any;
  colors: Record<string, string>;
  teamID: string;
  timezone: string;
  filters: Record<string, IMeasurementFilter>;
  dynamicFilters: IMeasurementDynamicFilter[];
  chart: IMeasurementChart;
  table: IMeasurementTable;
}

const MeasurementDrillColors = {
  blue: '#5d66fa',
  orangeRed: '#ff4e29',
  cyan: '#acf8f4',
  mustard: '#f3c628',
  mahogany: '#a75e6e',
  blueGray: '#84bcef',
  darkPeach: '#f4b583',
  turquoise: '#00b7a9',
  seaGreen: '#85ffc7',
  ochre: '#e6dfa4',
  pink: '#ff9cef',
  purple: '#6f60dd',
};

export const measurementDrillConfig = {
  defaultProps: {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: (theme: any) => ({
      section: {
        marginBottom: theme.spacing.unit,
      },
      alignRight,
      paper: {
        paddingLeft: theme.spacing.unit * 2,
        paddingRight: theme.spacing.unit * 2,
        paddingTop: theme.spacing.unit * 2,
        paddingBottom: theme.spacing.unit,
        boxShadow: '0px 3px 4px #E2E2E2',
      },
      graphPaper: {
        padding: 8,
        marginBottom: 15,
        boxShadow: '0px 3px 4px #E2E2E2',
      },
      selectedPaper: {
        ...theme.mixins.gutters(),
        boxShadow: '0px 3px 4px #E2E2E2',
      },
      notSelectedPaper: {
        ...theme.mixins.gutters(),
      },
      dashboardCard: {
        backgroundColor: 'white',
        padding: 10,
      },
      title: {
        fontWeight: 400,
        fontSize: 12,
        color: '#292F36',
      },
      dashboardCardValue: {
        fontWeight: 600,
        textAlign: 'left',
        fontSize: 20,
        color: '#292F36',
      },
      dashboardCardComparePositive: {
        fontWeight: 400,
        textAlign: 'left',
        fontSize: 12,
        color: '#4FC2B5',
      },
      dashboardCardCompareNegative: {
        fontWeight: 400,
        textAlign: 'left',
        fontSize: 12,
        color: '#F05251',
      },
      densityRangeSelected: {
        fontSize: 12,
        fontWeight: 500,
        color: '#6a686a',
        cursor: 'pointer',
      },
      densityRangeNotSelected: {
        fontSize: 12,
        fontWeight: 500,
        color: '#cecdcf',
        cursor: 'pointer',
      },
      dashboardToggleSelected: {
        fontSize: 12,
        fontWeight: 500,
        cursor: 'pointer',
      },
      dashboardToggleNotSelected: {
        fontSize: 12,
        fontWeight: 500,
        color: '#cecdcf',
        cursor: 'pointer',
      },
      chartName: {
        fontSize: 16,
        fontWeight: 500,
      },
      redArrow: {
        transform: 'rotate(180deg)',
      },
      list: {
        marginTop: '20px',
        paddingLeft: '0',
        paddingTop: '0',
        paddingBottom: '0',
        marginBottom: '0',
        listStyle: 'none',
        position: 'unset',
      },
      appbar: {
        position: 'sticky',
        top: 0,
        marginLeft: 16,
        backgroundColor: primaryColor[0],
      },
      appbarTitle: {
        color: whiteColor,
        flexGrow: 1,
      },
      mainPanel: {
        position: 'relative',
        float: 'right',
        width: `calc(100vw - ${drawerWidth}px)`,
        backgroundColor,
      },
      content: {
        padding: '0',
        minHeight: 'calc(100vh - 64px)',
      },
      container,
      mainTab: {
        color: '#707070',
        textDecoration: 'none',
        fontSize: '14px',
        fontWeight: '500',
        paddingTop: '4px',
        pointer: 'cursor',
      },
      mainTabSelected: {
        color: '#5D66FA',
        fontSize: '14px',
        fontWeight: '500',
        paddingTop: '4px',
        pointer: 'cursor',
      },
      unselectedTimeStamp: {
        '&:hover': {
          background: '#eee 0% 0% no-repeat padding-box',
          borderRadius: '5px',
          color: '#555555',
          fontSize: '12px',
        },
        color: '#555555',
        textDecoration: 'none',
        fontSize: '12px',
      },
      selectedTimeStamp: {
        background: '#eee 0% 0% no-repeat padding-box',
        color: '#292F36',
        fontSize: '12px',
        fontWeight: '700',
      },
      dashboardCardValueDisabled: {
        fontSize: 20,
        fontWeight: 500,
        color: '#cecdcf',
      },
      paperCards: {
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 0,
        paddingRight: 0,
        boxShadow: '0px 3px 4px #E2E2E2',
        minHeight: 85,
      },
      paperCardsDisabled: {
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 0,
        paddingRight: 0,
        boxShadow: '0px 3px 4px #E2E2E2',
        backgroundColor: '#EFEFEF',
        minHeight: 85,
      },
      dashboardCardTitleDisabled: {
        fontSize: 12,
        fontWeight: 500,
        color: '#cecdcf',
      },
      dashboardCardTitleDisabledLarge: {
        fontSize: 20,
        fontWeight: 500,
        color: '#cecdcf',
      },
      root: {
        '.MuiTab-textColorInherit.Mui-selected': {
          color: 'white',
        },
      },
    }),
  },
  colors: MeasurementDrillColors,
  teamID: '15661',
  timezone: 'America/New_York',
  filters: {
    Formats: {
      menuTitle: 'Formats',
      list: {
        '001': {
          ID: '001',
          title: 'Display',
          selected: false,
          values: ['111', '221', '121', '231', '131'],
        },
        '002': {
          ID: '002',
          title: 'Video',
          selected: false,
          values: ['252', '112', '222', '122', '232', '132'],
        },
      },
    },
    'Device Types': {
      menuTitle: 'Device Types',
      list: {
        101: {
          ID: '101',
          title: 'Desktop',
          selected: false,
          values: ['111', '112'],
        },
        102: {
          ID: '102',
          title: 'Mobile',
          selected: false,
          values: ['221', '222', '121', '122'],
        },
        103: {
          ID: '103',
          title: 'Tablet',
          selected: false,
          values: ['231', '232', '131', '132'],
        },
        104: {
          ID: '104',
          title: 'Connected TV',
          selected: false,
          values: ['252'],
        },
      },
    },
  },
  dynamicFilters: [
    // DELIVERY TREE
    { menuTitle: 'Advertisers', graph: 'graph26.sql', showID: true, idKey: 'AdvertiserIDs' },
    { menuTitle: 'Orders', graph: 'graph27.sql', showID: true, idKey: 'OrderIDs' },
    { menuTitle: 'Line Items', graph: 'graph28.sql', showID: true, idKey: 'LineItemIDs' },
    { menuTitle: 'Creatives', graph: 'graph29.sql', showID: true, idKey: 'CreativeIDs' },
    // INVENTORY TREE
    { menuTitle: 'Ad Units', graph: 'graph30.sql', showID: true, idKey: 'AdUnitIDs' },
  ],
  chart: {
    title: 'Insert Title Here',
    graph: 'graph31.sql',
  },
  table: {
    graph: 'graph31.sql',
    groupBys: [
      {
        dataKey: 'AdvertiserName',
        label: 'Advertisers',
        paramKey: 'Advertisers',
        idKey: 'AdvertiserID',
        childKey: 'Orders',
        tree: 'Delivery',
        nodeLevel: 0,
        isDefault: true,
      },
      {
        dataKey: 'OrderName',
        label: 'Orders',
        paramKey: 'Orders',
        idKey: 'OrderID',
        childKey: 'Line_Items',
        tree: 'Delivery',
        nodeLevel: 1,
      },
      {
        dataKey: 'LineItemName',
        label: 'Line Items',
        paramKey: 'Line_Items',
        idKey: 'LineItemID',
        childKey: 'Creatives',
        tree: 'Delivery',
        nodeLevel: 2,
      },
      {
        dataKey: 'CreativeName',
        label: 'Creatives',
        paramKey: 'Creatives',
        idKey: 'CreativeID',
        tree: 'Delivery',
        nodeLevel: 3,
      },
      {
        dataKey: 'AdUnitName',
        label: 'Ad Units',
        paramKey: 'Ad_Units',
        idKey: 'AdUnitID',
        tree: 'Inventory',
        nodeLevel: 0,
      },
    ],
    columns: [
      { dataKey: 'TotalImps', label: 'Total Tracked Ads', renderType: 'bignumber' },
      { dataKey: 'RenderedImps', label: 'Impressions (Rendered)', renderType: 'bignumber' },
      { dataKey: 'MeasuredRate', label: 'Measured Rate', renderType: 'percent' },
      {
        dataKey: 'ViewabilityRate',
        label: 'Viewable Rate (Out Of Measured Ads)',
        renderType: 'percent',
      },
      { dataKey: 'IVTRate', label: 'Invalid Traffic Rate', renderType: 'percent' },
      { dataKey: 'AdBlockRate', label: 'Ad Block Rate', renderType: 'percent' },
    ],
  },
} as IMeasurementConfig;
